import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Button,
    Fade,
    Grow
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Description as FileTextIcon,
    PictureAsPdf as FilePdfIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon,
    Visibility as EyeIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const ModernCVUpload = ({ onFilesAdded, onDeleteFile }) => {
    const [fileList, setFileList] = useState([]);
    const fileListRef = useRef(fileList);

    // Keep ref in sync with state
    useEffect(() => {
        fileListRef.current = fileList;
        console.log('Current CV file list:', fileList);
    }, [fileList]);

    const onDrop = useCallback(acceptedFiles => {
        const filteredFiles = acceptedFiles.filter(file => {
            // Check if file already exists to prevent duplicates
            const isDuplicate = fileListRef.current.some(existingFile =>
                existingFile.name === file.name && existingFile.size === file.size
            );

            if (isDuplicate) {
                console.log('Duplicate file detected:', file.name);
                return false;
            }

            return file.type === 'application/pdf' ||
                file.type === 'application/msword' ||
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        });

        if (filteredFiles.length !== acceptedFiles.length) {
            console.error('Some files were filtered out (duplicates or invalid types).');
        }

        if (filteredFiles.length > 0) {
            const newFiles = filteredFiles.map(file => ({
                uid: file.name + '_' + Date.now() + '_' + Math.random(),
                name: file.name,
                status: 'done',
                file: file,
                type: file.type,
                size: file.size,
                url: URL.createObjectURL(file)
            }));

            console.log('Adding CV files:', newFiles.map(f => f.name));
            setFileList(prev => [...prev, ...newFiles]);
            onFilesAdded(filteredFiles);
        }
    }, [onFilesAdded]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
        },
        multiple: true, // Allow multiple files for CV upload
    });

    const handleDelete = (fileToDelete) => {
        console.log('Deleting CV file:', fileToDelete.name);
        // Clean up object URL to prevent memory leaks
        if (fileToDelete.url) {
            URL.revokeObjectURL(fileToDelete.url);
        }

        setFileList(prev => prev.filter(file => file.uid !== fileToDelete.uid));
        onDeleteFile(fileToDelete);
    };

    const handlePreview = (file) => {
        if (file.url) {
            window.open(file.url, '_blank');
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType === 'application/pdf') {
            return <FilePdfIcon sx={{ color: '#FF6B6B', fontSize: 24 }} />;
        }
        return <FileTextIcon sx={{ color: '#4DABF7', fontSize: 24 }} />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Typography
                variant="h2"
                sx={{
                    color: '#FFFFFF',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 3,
                }}
            >
                Upload CVs
            </Typography>

            {/* File List - Show when files exist */}
            {fileList.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <List sx={{ width: '100%', p: 0, maxHeight: 300, overflowY: 'auto' }}>
                        {fileList.map((file) => (
                            <Fade key={file.uid} in={true} timeout={500}>
                                <ListItem
                                    sx={{
                                        mb: 1.5,
                                        p: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                borderRadius: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {getFileIcon(file.type)}
                                        </Box>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                sx={{
                                                    color: '#FFFFFF',
                                                    fontWeight: 500,
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {file.name}
                                            </Typography>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                                                    {formatFileSize(file.size)}
                                                </Typography>
                                                <CheckCircleIcon sx={{ color: '#10B981', fontSize: 16 }} />
                                                <Typography sx={{ color: '#10B981', fontSize: '0.75rem' }}>
                                                    Uploaded successfully
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            onClick={() => handlePreview(file)}
                                            sx={{
                                                mr: 1,
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {
                                                    color: '#4DABF7',
                                                    backgroundColor: 'rgba(77, 171, 247, 0.1)',
                                                }
                                            }}
                                        >
                                            <EyeIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(file)}
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {
                                                    color: '#FF4444',
                                                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </Fade>
                        ))}
                    </List>
                </Box>
            )}

            {/* Upload Zone - Always visible, but smaller when files exist */}
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? '#FA370A' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: fileList.length > 0 ? 3 : 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive
                        ? 'rgba(250, 55, 10, 0.08)'
                        : 'rgba(255, 255, 255, 0.02)',
                    transition: 'all 0.2s ease',
                    minHeight: fileList.length > 0 ? 120 : 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    }
                }}
            >
                <input {...getInputProps()} />
                <Box>
                    {/* Icon - smaller when files exist */}
                    <Box
                        sx={{
                            width: fileList.length > 0 ? 48 : 64,
                            height: fileList.length > 0 ? 48 : 64,
                            backgroundColor: '#FA370A',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        {fileList.length > 0 ? (
                            <AddIcon sx={{ fontSize: 24, color: 'white' }} />
                        ) : (
                            <CloudUploadIcon sx={{ fontSize: 32, color: 'white' }} />
                        )}
                    </Box>

                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            mb: 0.5,
                            fontWeight: 500,
                            fontSize: fileList.length > 0 ? '1rem' : '1.1rem',
                        }}
                    >
                        {isDragActive
                            ? 'Drop your files here'
                            : fileList.length > 0
                                ? 'Add More CVs'
                                : 'Upload CVs'
                        }
                    </Typography>

                    {fileList.length === 0 && (
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                mb: 2,
                                fontSize: '0.875rem'
                            }}
                        >
                            Drag and drop or{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: '#FA370A',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    }
                                }}
                            >
                                browse files
                            </Box>
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Chip
                            label="PDF"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label="DOC"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label="DOCX"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                            • Max 50MB
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ModernCVUpload;